import React from "react";
import Icon from "./AppIcon";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.log("Error caught by ErrorBoundary:", error, errorInfo);
    this.setState({ 
      error, 
      errorInfo,
      hasError: true 
    });
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error?.name === 'ChunkLoadError' || 
                          this.state.error?.message?.includes('Loading chunk') ||
                          this.state.error?.message?.includes('Loading CSS chunk');
      
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100">
          <div className="text-center p-8 max-w-md bg-white rounded-2xl shadow-elevation-2 border border-gray-200">
            <div className="flex justify-center items-center mb-6">
              {isChunkError ? (
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <Icon name="Wifi" size={32} className="text-amber-600" />
                </div>
              ) : (
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <Icon name="AlertCircle" size={32} className="text-red-600" />
                </div>
              )}
            </div>
            
            <div className="flex flex-col gap-3 text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-800">
                {isChunkError ? "Connection Issue" : "Something went wrong"}
              </h1>
              <p className="text-gray-600 text-base leading-relaxed">
                {isChunkError 
                  ? "We're having trouble loading the page content. This might be due to a network issue or an app update."
                  : "We encountered an unexpected error while processing your request."
                }
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              {isChunkError && this.state.retryCount < 3 ? (
                <button
                  onClick={this.handleRetry}
                  className="bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-lg flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                >
                  <Icon name="RotateCcw" size={18} />
                  Retry ({3 - this.state.retryCount} attempts left)
                </button>
              ) : (
                <button
                  onClick={this.handleGoHome}
                  className="bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-lg flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                >
                  <Icon name="Home" size={18} />
                  Go to Home
                </button>
              )}
              
              <button
                onClick={() => window.location.reload()}
                className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-6 rounded-lg flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
              >
                <Icon name="RefreshCw" size={18} />
                Refresh Page
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left bg-gray-50 rounded-lg p-4">
                <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                  Error Details (Dev Mode)
                </summary>
                <pre className="text-xs text-gray-600 overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;